import { useCallback, useEffect, useRef, useState } from 'react';

import { create } from 'zustand';

import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';

interface OnboardingMachine {
  send: (event: { type: string }) => void;
}

interface UseOnboardingUnsavedChangesGuardReturn {
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
  /** Wraps the back button click — shows modal if dirty, otherwise sends BACK */
  handleBack: () => void;
  /** Wraps the next/finish action — bypasses guard since data is being saved */
  handleClickNext: () => void;
  /** Whether the unsaved changes modal should be shown */
  isUnsavedModalOpen: boolean;
  handleUnsavedModalCancel: () => void;
  handleUnsavedModalDiscard: () => void;
}

interface OnboardingUnsavedChangesState {
  isDirty: boolean;
  setIsDirty: (isDirty: boolean) => void;
  reset: () => void;
}

export const useOnboardingUnsavedChangesStore = create<OnboardingUnsavedChangesState>(set => ({
  isDirty: false,
  setIsDirty: isDirty => set({ isDirty }),
  reset: () => set({ isDirty: false }),
}));

/**
 * Combines useUnsavedChangesGuard with XState onboarding navigation.
 *
 * Handles two sources of navigation that need guarding:
 * 1. Browser back/forward and tab close — handled by useUnsavedChangesGuard
 * 2. Footer back button — handled manually since XState events bypass React Router
 *
 * The next/finish button always bypasses the guard (data is saved before navigating).
 */
export function useOnboardingUnsavedChangesGuard(
  onboardingMachine: OnboardingMachine | undefined
): UseOnboardingUnsavedChangesGuardReturn {
  const [isDirty, setIsDirty] = useState(false);
  const setSidebarIsDirty = useOnboardingUnsavedChangesStore(state => state.setIsDirty);
  const resetSidebarDirty = useOnboardingUnsavedChangesStore(state => state.reset);
  const isDirtyRef = useRef(false);
  isDirtyRef.current = isDirty;

  const [isBackModalOpen, setIsBackModalOpen] = useState(false);

  useEffect(() => {
    setSidebarIsDirty(isDirty);

    return () => {
      resetSidebarDirty();
    };
  }, [isDirty, setSidebarIsDirty, resetSidebarDirty]);

  const { isModalOpen, handleModalDiscard, handleModalCancel, allowNavigation } =
    useUnsavedChangesGuard({
      isDirty,
      onDiscard: () => setIsDirty(false),
    });

  const handleBack = useCallback(() => {
    if (isDirtyRef.current) {
      setIsBackModalOpen(true);
      return;
    }
    onboardingMachine?.send({ type: 'BACK' });
  }, [onboardingMachine]);

  const handleBackDiscard = useCallback(() => {
    setIsBackModalOpen(false);
    setIsDirty(false);
    allowNavigation();
    onboardingMachine?.send({ type: 'BACK' });
  }, [allowNavigation, onboardingMachine]);

  const handleClickNext = useCallback(() => {
    allowNavigation();
    onboardingMachine?.send({ type: 'NEXT' });
  }, [allowNavigation, onboardingMachine]);

  const isUnsavedModalOpen = isModalOpen || isBackModalOpen;

  const handleUnsavedModalCancel = useCallback(() => {
    handleModalCancel();
    setIsBackModalOpen(false);
  }, [handleModalCancel]);

  const handleUnsavedModalDiscard = useCallback(() => {
    if (isBackModalOpen) {
      handleBackDiscard();
    } else {
      handleModalDiscard();
    }
  }, [isBackModalOpen, handleBackDiscard, handleModalDiscard]);

  return {
    isDirty,
    setIsDirty,
    handleBack,
    handleClickNext,
    isUnsavedModalOpen,
    handleUnsavedModalCancel,
    handleUnsavedModalDiscard,
  };
}
